import React, { useState } from 'react';
import { 
  MessageSquare, 
  CheckCircle, 
  Clock, 
  Calendar,
  Trash2,
  Eye,
  Filter,
  Search,
  X
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminSystemMessages } from '../../hooks/useAdminSystemMessages';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';

const AdminSystemMessages = () => {
  const toast = useToastContext();
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [detailModalMessage, setDetailModalMessage] = useState(null);
  const {
    messages,
    pagination,
    statistics,
    isLoading,
    error,
    filters,
    typeOptions,
    readStatusOptions,
    handleFilterChange,
    deleteMessage,
    isDeleting,
  } = useAdminSystemMessages();

  const handleRowSelect = (selectedRows) => {
    setSelectedMessages(selectedRows);
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedMessages.length} message(s)?`)) {
      try {
        for (const message of selectedMessages) {
          await deleteMessage(message.id);
        }
        setSelectedMessages([]);
        toast.success(null, `${selectedMessages.length} message(s) deleted successfully`);
      } catch (error) {
        toast.error(null, 'Failed to delete messages');
      }
    }
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      'booking_placed': 'bg-blue-100 text-blue-800',
      'payment_received': 'bg-green-100 text-green-800',
      'user_registered': 'bg-purple-100 text-purple-800',
      'vendor_subscribed': 'bg-orange-100 text-orange-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      'booking_placed': 'Booking Placed',
      'payment_received': 'Payment Received',
      'user_registered': 'User Registered',
      'vendor_subscribed': 'Vendor Subscribed',
    };
    return labels[type] || type;
  };

  // Define table columns - reduced for cleaner layout
  const columns = [
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: 'w-50',
      render: (message) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(message.type)}`}>
          {getTypeLabel(message.type)}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-56',
      render: (message) => (
        <p className="text-sm font-medium text-gray-900 line-clamp-2 min-w-0" title={message.title || message.message}>
          {message.title || message.message?.substring(0, 60) || 'N/A'}
        </p>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-48',
      render: (message) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-900">
            {formatDate(message.created_at, 'medium')}
          </span>
        </div>
      )
    },
    {
      key: 'is_read',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      render: (message) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          message.is_read ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {message.is_read ? <><CheckCircle className="w-3 h-3 mr-1" />Read</> : <><Clock className="w-3 h-3 mr-1" />Unread</>}
        </span>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      onClick: (message) => setDetailModalMessage(message),
      className: 'text-blue-600 hover:text-blue-800'
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (message) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
          deleteMessage(message.id).then(() => {
            toast.success(null, 'Message deleted successfully');
            if (detailModalMessage?.id === message.id) setDetailModalMessage(null);
          }).catch(() => {
            toast.error(null, 'Failed to delete message');
          });
        }
      },
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading messages: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Messages</h1>
          <p className="text-sm text-gray-500 mt-1">View and manage system-generated messages</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total || 0}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unread</p>
                <p className="text-2xl font-bold text-yellow-600">{statistics.unread || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today</p>
                <p className="text-2xl font-bold text-blue-600">{statistics.today || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">This Week</p>
                <p className="text-2xl font-bold text-green-600">{statistics.this_week || 0}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-green-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <SearchSelect
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              options={typeOptions}
              placeholder="All Types"
              searchable={false}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <SearchSelect
              value={filters.is_read}
              onChange={(value) => handleFilterChange('is_read', value)}
              options={readStatusOptions}
              placeholder="All Messages"
              searchable={false}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <DatePicker
              selected={filters.date_from ? new Date(filters.date_from) : null}
              onChange={(date) => handleFilterChange('date_from', date ? date.toISOString().split('T')[0] : '')}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <DatePicker
              selected={filters.date_to ? new Date(filters.date_to) : null}
              onChange={(date) => handleFilterChange('date_to', date ? date.toISOString().split('T')[0] : '')}
              className="w-full"
            />
          </div>
        </div>
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or message..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-lg shadow min-h-[28rem] flex flex-col">
        <DataTable
          data={messages}
          columns={columns}
          actions={actions}
          isLoading={isLoading}
          onRowSelect={handleRowSelect}
          selectable={true}
          pagination={pagination}
          compact={true}
        />
      </div>

      {/* Message Detail Modal */}
      {detailModalMessage && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetailModalMessage(null)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Message Details</h3>
              <button
                onClick={() => setDetailModalMessage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)] space-y-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Type</p>
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(detailModalMessage.type)}`}>
                  {getTypeLabel(detailModalMessage.type)}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Title</p>
                <p className="text-sm font-medium text-gray-900">{detailModalMessage.title || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Message</p>
                <p className="text-sm text-gray-700">{detailModalMessage.message || 'N/A'}</p>
              </div>
              {detailModalMessage.data && Object.keys(detailModalMessage.data).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Details</p>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    {Object.entries(detailModalMessage.data).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm gap-4">
                        <span className="font-medium text-gray-600">{key}:</span>
                        <span className="text-gray-900 text-right break-all">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <span>
                  <strong>Date:</strong> {formatDate(detailModalMessage.created_at, 'medium')} {formatTime(detailModalMessage.created_at)}
                </span>
                {detailModalMessage.read_at && (
                  <span>
                    <strong>Read at:</strong> {formatDate(detailModalMessage.read_at, 'medium')} {formatTime(detailModalMessage.read_at)}
                  </span>
                )}
                {detailModalMessage.triggered_by && (
                  <span>
                    <strong>Triggered by:</strong> {detailModalMessage.triggered_by_type} (ID: {detailModalMessage.triggered_by})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedMessages.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">
              {selectedMessages.length} message(s) selected
            </span>
            <Button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSystemMessages;

